import { Button, Anchor } from "@newsela/angelou";
import { useRouter } from "next/router";

import { withTrackEvent } from "@newsela/snitch";
import { Assignment } from "@/types/models/Assignment.interface";
import { StreamProperties } from "@/types/models/EventProperties.interface";
import contentTypes, { ContentType } from "@/view/constants/contentTypes";
import {
  ASSIGNMENT_MODE,
  currentAssignmentMode,
} from "@/view/utils/currentAssignmentMode";
import GetAssignmentUrl from "@/view/utils/GetAssignmentUrl";
import { rootCss, mobileSaveCss } from "./AssignButton.style";

interface AssignButtonInterface {
  contentType?: ContentType;
  updatedAssignment?: Pick<Assignment, "content_id" | "is_draft">;
  isMobile?: boolean | undefined;
  isSaving: boolean | undefined;
  onClick?: () => void;
  streamProperties?: StreamProperties | null;
  triggerRef: React.Ref<HTMLButtonElement> | null;
  showMobileButton?: boolean | undefined;
}

const ButtonWithTrackEvent = withTrackEvent(
  Button,
) as React.ForwardRefExoticComponent<
  React.ComponentProps<typeof Button> & React.RefAttributes<HTMLButtonElement>
>;

const AnchorWithTrackEvent = withTrackEvent(Anchor);

const AssignButton = ({
  contentType,
  updatedAssignment,
  isMobile,
  isSaving = false,
  onClick,
  streamProperties,
  triggerRef,
  showMobileButton = false,
}: AssignButtonInterface) => {
  const { asPath, query } = useRouter();
  const { contentId } = query;

  const assignmentMode = currentAssignmentMode(updatedAssignment);
  const isUpdateMode = assignmentMode === ASSIGNMENT_MODE.UPDATE;

  const eventProperties = {
    objectType: "Assignment",
    actionPrefix: "Initiate",
    actionName: "Navigate",
    streamProperties: {
      ...streamProperties,
      description: "Open create assignment flow",
    },
  };

  const buttonText = () => {
    let text;
    if (isSaving) {
      text = "Saving...";
    } else {
      text = isUpdateMode ? "Save changes" : "Assign";
    }
    return text;
  };

  const createAssignmentUrl = GetAssignmentUrl(contentId, asPath);
  const contentIsVideo = contentType === contentTypes.INTERACTIVE_VIDEO;
  const isGenerationGeniusVideo =
    contentType === contentTypes.GENERATION_GENIUS_VIDEO;

  const renderButtonOrAnchor = () => {
    if (contentIsVideo) {
      return (
        // @ts-expect-error Property 'children' does not exist on type
        <AnchorWithTrackEvent
          dataProps={{
            "data-qa-selector": showMobileButton
              ? "save-assignment-mobile-button"
              : "save-assignment-button",
          }}
          href={createAssignmentUrl}
          openInNewTab
          styleAnchorAsButton={{
            flavor: Button.flavors.primary,
          }}
          {...eventProperties}
        >
          {buttonText()}
        </AnchorWithTrackEvent>
      );
    } else {
      return (
        // @ts-expect-error Property 'children' does not exist on type
        <ButtonWithTrackEvent
          __cssFor={{
            root: showMobileButton
              ? ""
              : rootCss(
                  !!updatedAssignment?.content_id,
                  isUpdateMode,
                  isMobile,
                ),
          }}
          dataProps={{
            "data-qa-selector": showMobileButton
              ? `${isGenerationGeniusVideo ? "generation-genius-" : ""}save-assignment-mobile-button`
              : `${isGenerationGeniusVideo ? "generation-genius-" : ""}save-assignment-button`,
          }}
          flavor={Button.flavors.primary}
          onClick={onClick}
          ref={triggerRef}
          {...eventProperties}
          isFullWidth
          disabled={isSaving}
        >
          {buttonText()}
        </ButtonWithTrackEvent>
      );
    }
  };

  return (
    <div css={showMobileButton ? mobileSaveCss : ""}>
      {renderButtonOrAnchor()}
    </div>
  );
};

export default AssignButton;
